/**
 * Копирование / вставка диапазона: TSV для обмена с Excel/Calc,
 * внутренний снимок — формулы со сдвигом ссылок + заливка/стены.
 */
import { cellKey, shiftFormulaRefs } from './formulaEngine.js';
import {
  MAX_FULL_COLS,
  fillAntAcrossRanges,
  getFill,
  getWalls,
  selectionBounds,
  setCellsValues,
} from './cellModel.js';

/** Нормализация TSV для сравнения с системным буфером (CRLF / хвостовой \\n). */
export function normalizeTsvText(text) {
  return String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n+$/g, '');
}

export function isSingleCellClipboard(payload) {
  if (!payload?.grid?.length) return false;
  const rows = payload.rows ?? payload.grid.length;
  const cols = payload.cols ?? payload.grid[0]?.length ?? 0;
  return rows === 1 && cols === 1;
}

/**
 * Вставить одну клетку во все ranges.
 * Если есть origin (наш буфер / муравьи) — формулы со сдвигом; иначе как есть.
 */
export function pasteSingleAcrossRanges(state, payload, ranges, { origin: originOverride = null } = {}) {
  if (!isSingleCellClipboard(payload) || !ranges?.length) return state;
  const value = payload.grid[0][0] ?? '';
  const fill = payload.fills?.[0]?.[0] ?? null;
  const origin = originOverride || payload.origin || null;
  if (origin) {
    return fillAntAcrossRanges(state, {
      origin,
      value,
      fill,
      bounds: {
        r0: origin.row,
        r1: origin.row,
        c0: origin.col,
        c1: origin.col,
      },
    }, ranges);
  }
  // Внешний TSV без origin — одинаковый текст во все клетки (как Excel)
  const updates = [];
  const seen = new Set();
  for (const raw of ranges) {
    const b = selectionBounds(raw);
    if (!b) continue;
    for (let r = b.r0; r <= b.r1; r += 1) {
      for (let c = b.c0; c <= b.c1; c += 1) {
        const key = cellKey(r, c);
        if (seen.has(key)) continue;
        seen.add(key);
        updates.push({ row: r, col: c, value });
      }
    }
  }
  let next = setCellsValues(state, updates);
  if (payload.fills) {
    const fills = { ...(next.fills || {}) };
    for (const { row, col } of updates) {
      const key = cellKey(row, col);
      if (fill) fills[key] = fill;
      else delete fills[key];
    }
    next = { ...next, fills };
  }
  return next;
}

/** Экранирование ячейки для TSV (таб / перевод строки / кавычки). */
export function escapeTsvCell(value) {
  const s = value == null ? '' : String(value);
  if (/[\t\n\r"]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function parseTsv(text) {
  const raw = String(text || '').replace(/^\uFEFF/, '');
  if (raw === '') return [['']];
  const rows = [];
  let row = [];
  let cell = '';
  let i = 0;
  let inQuotes = false;
  while (i < raw.length) {
    const ch = raw[i];
    if (inQuotes) {
      if (ch === '"') {
        if (raw[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === '\t') {
      row.push(cell);
      cell = '';
      i += 1;
      continue;
    }
    if (ch === '\n' || ch === '\r') {
      row.push(cell);
      cell = '';
      rows.push(row);
      row = [];
      if (ch === '\r' && raw[i + 1] === '\n') i += 1;
      i += 1;
      continue;
    }
    cell += ch;
    i += 1;
  }
  row.push(cell);
  // trailing newline → пустая последняя строка не нужна
  if (!(row.length === 1 && row[0] === '' && rows.length > 0)) {
    rows.push(row);
  }
  const width = Math.max(1, ...rows.map((r) => r.length));
  return rows.map((r) => {
    const next = r.slice();
    while (next.length < width) next.push('');
    return next;
  });
}

export function selectionToClipboard(state, sel) {
  const b = selectionBounds(sel);
  if (!b) return null;
  const grid = [];
  const fills = [];
  const walls = [];
  const tsvLines = [];
  for (let r = b.r0; r <= b.r1; r += 1) {
    const line = [];
    const fillRow = [];
    const wallRow = [];
    for (let c = b.c0; c <= b.c1; c += 1) {
      const raw = state?.cells?.[cellKey(r, c)] ?? '';
      line.push(raw);
      fillRow.push(getFill(state, r, c));
      wallRow.push(getWalls(state, r, c));
    }
    grid.push(line);
    fills.push(fillRow);
    walls.push(wallRow);
    tsvLines.push(line.map(escapeTsvCell).join('\t'));
  }
  return {
    tsv: tsvLines.join('\n'),
    grid,
    fills,
    walls,
    origin: { row: b.r0, col: b.c0 },
    rows: b.r1 - b.r0 + 1,
    cols: b.c1 - b.c0 + 1,
  };
}

/**
 * Вставить grid в (startRow, startCol).
 * Если есть origin (наш буфер) — формулы сдвигаются относительно исходных клеток.
 * Иначе формулы из TSV вставляются как написаны.
 */
export function pasteClipboard(state, startRow, startCol, payload, { adjustFormulas = true } = {}) {
  if (!payload?.grid?.length) return state;
  const grid = payload.grid;
  const nRows = grid.length;
  const nCols = Math.max(0, ...grid.map((r) => r.length));
  if (nRows === 0 || nCols === 0) return state;

  const endRow = startRow + nRows - 1;
  const endCol = startCol + nCols - 1;
  let next = {
    ...state,
    rows: Math.max(state.rows || 1, endRow + 1),
    cols: Math.min(MAX_FULL_COLS, Math.max(state.cols || 1, endCol + 1)),
  };

  const origin = payload.origin;
  const updates = [];
  for (let i = 0; i < nRows; i += 1) {
    for (let j = 0; j < nCols; j += 1) {
      const srcVal = grid[i][j] ?? '';
      const destR = startRow + i;
      const destC = startCol + j;
      let value = srcVal;
      if (
        adjustFormulas
        && typeof srcVal === 'string'
        && srcVal.startsWith('=')
        && origin
      ) {
        const srcR = origin.row + i;
        const srcC = origin.col + j;
        value = shiftFormulaRefs(srcVal, destR - srcR, destC - srcC);
      }
      updates.push({ row: destR, col: destC, value });
    }
  }
  next = setCellsValues(next, updates);

  if (payload.fills || payload.walls) {
    const fills = { ...(next.fills || {}) };
    const walls = { ...(next.walls || {}) };
    for (let i = 0; i < nRows; i += 1) {
      for (let j = 0; j < nCols; j += 1) {
        const key = cellKey(startRow + i, startCol + j);
        const fill = payload.fills?.[i]?.[j] ?? null;
        if (fill) fills[key] = fill;
        else delete fills[key];
        const wall = payload.walls?.[i]?.[j] ?? null;
        if (wall && (wall.t || wall.r || wall.b || wall.l)) {
          walls[key] = { ...wall };
        } else {
          delete walls[key];
        }
      }
    }
    next = { ...next, fills, walls };
  }

  return next;
}

export function clipboardFromTsv(text) {
  const grid = parseTsv(text);
  return {
    tsv: String(text || ''),
    grid,
    fills: null,
    walls: null,
    origin: null,
    rows: grid.length,
    cols: grid[0]?.length || 0,
  };
}

/** Очистить диапазон (для Вырезать). */
export function clearSelectionCells(state, sel) {
  const b = selectionBounds(sel);
  if (!b) return state;
  const updates = [];
  for (let r = b.r0; r <= b.r1; r += 1) {
    for (let c = b.c0; c <= b.c1; c += 1) {
      updates.push({ row: r, col: c, value: '' });
    }
  }
  let next = setCellsValues(state, updates);
  const fills = { ...(next.fills || {}) };
  const walls = { ...(next.walls || {}) };
  for (let r = b.r0; r <= b.r1; r += 1) {
    for (let c = b.c0; c <= b.c1; c += 1) {
      const key = cellKey(r, c);
      delete fills[key];
      delete walls[key];
    }
  }
  return { ...next, fills, walls };
}
